/*
 * Concept A · incl./excl. BTW price toggle (whole site).
 * Display only — Shopify still charges the correct VAT at checkout.
 * All Verfsale products are the standard 21% rate (confirmed by the client),
 * so excl. price = incl. price / 1.21.
 *
 * Prices that live outside the standard Dawn ".price-item" markup (e.g. bespoke
 * sections) won't be picked up automatically — add the class/selector to
 * PRICE_SELECTOR below, or put a `data-vat-price` attribute on the price node.
 *
 * The live cart drawer is the UpCart app, which renders into a SHADOW DOM.
 * document.querySelectorAll cannot see inside it and a MutationObserver on
 * <body> never fires for it, so until Oct 2026 a shopper on excl. BTW saw the
 * ex-VAT price on the product page and the incl. price in the drawer — a 21%
 * jump on the way to checkout. We now walk the shadow root as a second root
 * and observe it separately, the same way bulkkorting-upcart.js does.
 */
(function () {
  'use strict';

  var COOKIE = 'vfs_vat_mode';
  var INCL = 'incl';
  var EXCL = 'excl';

  // Leaf nodes whose text is a single price. The header cart subtotal is handled
  // separately (in header.liquid) to avoid a fight with its own updater.
  var PRICE_SELECTOR = [
    '.price-item',
    '.totals__total-value',
    '.totals__subtotal-value',
    '.cart__subtotal',
    '.vs-cart__final-price',
    '.vs-cart__old-price',
    '.vs-cart__price',
    '.cn-card__price-old',
    /* UpCart drawer (inside the shadow root). The subtotal's class is generated
       by UpCart's build, so match on the stable part rather than the full hash. */
    '.upcart-internal-component-product-tile__price',
    '.upcart-internal-component-product-tile__price-compare-at',
    '[class*="cartSubtotalValue"]',
    '[data-vat-price]'
  ].join(',');

  /* Nodes that hold a price INSIDE other words, e.g. UpCart's "(Bespaar €7,00)".
     process() deliberately refuses these, so they get a narrower handler that
     rewrites only the number. Opt-in by selector — never applied broadly. */
  var EMBEDDED_PRICE_SELECTOR = [
    '.upcart-internal-component-product-tile__savings',
    '[data-vat-price-embedded]'
  ].join(',');

  var upcartRoot = null;
  var shadowObserver = null;
  var shadowPending;

  function readRate() {
    var el = document.querySelector('[data-vfs-vat]');
    var r = el ? parseFloat(el.getAttribute('data-vat-rate')) : NaN;
    if (!r || isNaN(r) || r <= 0) r = 21;
    return 1 + r / 100;
  }
  var DIVISOR = readRate();
  window.VFS_VAT_DIVISOR = DIVISOR;

  function getMode() {
    var m = document.cookie.match(/(?:^|;\s*)vfs_vat_mode=(incl|excl)/);
    return m ? m[1] : INCL;
  }
  function setCookie(mode) {
    document.cookie = COOKIE + '=' + mode + ';path=/;max-age=31536000;SameSite=Lax';
  }

  // "€19,99" | "€1.234,56" | "€ 50" -> integer cents, or null.
  function parseCents(text) {
    var cleaned = text.replace(/[^\d.,]/g, '');
    if (!cleaned) return null;
    var normalized;
    if (cleaned.indexOf(',') > -1) {
      normalized = cleaned.replace(/\./g, '').replace(',', '.');
    } else if (/^\d{1,3}(\.\d{3})+$/.test(cleaned)) {
      normalized = cleaned.replace(/\./g, '');
    } else {
      normalized = cleaned;
    }
    var val = parseFloat(normalized);
    if (isNaN(val)) return null;
    return Math.round(val * 100);
  }

  // 123456 -> "1.234,56" (Dutch grouping)
  function formatNL(cents) {
    var v = (cents / 100).toFixed(2).split('.');
    return v[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + v[1];
  }

  // Only convert if the node holds ONE clean price (no ranges, no extra words).
  function isCleanPrice(text) {
    if (!/\d/.test(text)) return false;
    if (/[–—]/.test(text)) return false; // en/em dash = price range
    // strip currency symbols, digits, separators and a trailing marker → must be empty
    return text.replace(/[€$£]|EUR|\d|[.,\s*]/gi, '') === '';
  }

  function process(el, mode) {
    var current = el.textContent;
    var trimmed = current.trim();

    // (Re)capture the incl. baseline when the text is not ours — first paint, or
    // an external update such as a variant change / AJAX cart refresh.
    if (el.getAttribute('data-vat-shown') !== trimmed) {
      if (!isCleanPrice(trimmed)) return;
      var cents = parseCents(trimmed);
      if (cents == null) return;
      el.setAttribute('data-vat-incl-cents', cents);
      el.setAttribute('data-vat-incl-text', trimmed);
      var pre = trimmed.match(/^[^\d]*/);
      el.setAttribute('data-vat-pre', pre ? pre[0] : '');
      el.setAttribute('data-vat-suf', trimmed.replace(/^.*\d/, ''));
    }

    var incl = parseInt(el.getAttribute('data-vat-incl-cents'), 10);
    if (isNaN(incl)) return;

    var out = (mode === EXCL)
      ? el.getAttribute('data-vat-pre') + formatNL(Math.round(incl / DIVISOR)) + el.getAttribute('data-vat-suf')
      : el.getAttribute('data-vat-incl-text');

    if (el.textContent !== out) el.textContent = out; // guard: no self-triggered mutations
    el.setAttribute('data-vat-shown', out.trim());
  }

  /* Same baseline/guard discipline as process(), but replaces just the first
     "€12,34" found and leaves the surrounding text untouched. */
  var EMBEDDED_RE = /(\d{1,3}(?:\.\d{3})*|\d+),(\d{2})/;

  function processEmbedded(el, mode) {
    var trimmed = el.textContent.trim();

    if (el.getAttribute('data-vat-shown') !== trimmed) {
      if (!EMBEDDED_RE.test(trimmed)) return;
      el.setAttribute('data-vat-incl-text', trimmed);
    }

    var base = el.getAttribute('data-vat-incl-text');
    if (!base) return;

    var out = base;
    if (mode === EXCL) {
      out = base.replace(EMBEDDED_RE, function (match) {
        var cents = parseCents(match);
        return cents == null ? match : formatNL(Math.round(cents / DIVISOR));
      });
    }

    if (el.textContent !== out) el.textContent = out;
    el.setAttribute('data-vat-shown', out.trim());
  }

  /* document plus the UpCart shadow root once it exists. */
  function roots() {
    return upcartRoot ? [document, upcartRoot] : [document];
  }

  function applyAll(mode, root) {
    var list = root ? [root] : roots();
    for (var r = 0; r < list.length; r++) {
      var nodes = list[r].querySelectorAll(PRICE_SELECTOR);
      for (var i = 0; i < nodes.length; i++) process(nodes[i], mode);
      var emb = list[r].querySelectorAll(EMBEDDED_PRICE_SELECTOR);
      for (var j = 0; j < emb.length; j++) processEmbedded(emb[j], mode);
    }
  }

  function updateUI(mode) {
    var btns = document.querySelectorAll('[data-vat-mode]');
    for (var i = 0; i < btns.length; i++) {
      var active = btns[i].getAttribute('data-vat-mode') === mode;
      btns[i].classList.toggle('is-active', active);
      btns[i].setAttribute('aria-pressed', active ? 'true' : 'false');
    }
  }

  function setMode(mode) {
    if (mode !== EXCL) mode = INCL;
    setCookie(mode);
    applyAll(mode);
    updateUI(mode);
    document.dispatchEvent(new CustomEvent('vfs:vat-change', { detail: { mode: mode } }));
  }

  window.VFSVat = {
    setMode: setMode,
    getMode: getMode,
    apply: function () { applyAll(getMode()); }
  };

  function init() {
    var mode = getMode();
    updateUI(mode);
    applyAll(mode);
    // let other price renderers (e.g. header cart subtotal) sync on first load
    document.dispatchEvent(new CustomEvent('vfs:vat-change', { detail: { mode: mode } }));

    document.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest ? e.target.closest('[data-vat-mode]') : null;
      if (!btn) return;
      e.preventDefault();
      setMode(btn.getAttribute('data-vat-mode'));
    });

    // Re-apply to prices injected/updated by AJAX (cart drawer, variant change, predictive search).
    var pending;
    new MutationObserver(function () {
      clearTimeout(pending);
      pending = setTimeout(function () { applyAll(getMode()); }, 150);
    }).observe(document.body, { childList: true, subtree: true, characterData: true });

    waitForUpcart();
  }

  /* UpCart mounts late and re-renders its whole tree on every cart change, so
     the shadow root needs its own observer — the <body> one above never sees it. */
  function attachShadowObserver() {
    if (shadowObserver || !upcartRoot) return;
    shadowObserver = new MutationObserver(function () {
      clearTimeout(shadowPending);
      shadowPending = setTimeout(function () { applyAll(getMode(), upcartRoot); }, 120);
    });
    shadowObserver.observe(upcartRoot, { childList: true, subtree: true, characterData: true });
    applyAll(getMode(), upcartRoot);
  }

  function findUpcartRoot() {
    if (upcartRoot) return true;
    var host = document.getElementById('upCart');
    if (host && host.shadowRoot) {
      upcartRoot = host.shadowRoot;
      attachShadowObserver();
      return true;
    }
    return false;
  }

  function waitForUpcart() {
    if (findUpcartRoot()) return;
    var obs = new MutationObserver(function () {
      if (findUpcartRoot()) obs.disconnect();
    });
    obs.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
